import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { LogzioTheme } from '@logz-pkg/enums';
import { lightV2Theme } from './light-v2.theme';
import { darkV2Theme } from './dark-v2.theme';
import { applyThemeToDocument } from './utils';

interface IThemeContext {
  currentTheme: LogzioTheme;
  toggleTheme: () => void;
  setTheme: (theme: LogzioTheme) => void;
}

export const ThemeContext = createContext<IThemeContext>({
  currentTheme: LogzioTheme.Light,
  toggleTheme: () => {},
  setTheme: () => {},
});

interface IThemeProviderProps {
  initialTheme?: LogzioTheme;
  theme?: any;
  children: ReactNode;
  targetDocument?: Document;
}

export const ThemeProvider: React.FC<IThemeProviderProps> = ({
  initialTheme = LogzioTheme.Light,
  theme,
  children,
  targetDocument = document,
}) => {
  const [currentTheme, setCurrentTheme] = useState<LogzioTheme>(initialTheme);

  let effectiveTheme: LogzioTheme;

  if (theme) {
    effectiveTheme = theme.name === 'dark' ? LogzioTheme.Dark : LogzioTheme.Light;
  } else {
    effectiveTheme = currentTheme;
  }

  const toggleTheme = useCallback(() => {
    setCurrentTheme(prevTheme => (prevTheme === LogzioTheme.Light ? LogzioTheme.Dark : LogzioTheme.Light));
  }, []);

  const setTheme = useCallback((theme: LogzioTheme) => {
    setCurrentTheme(theme);
  }, []);

  const themeObject = effectiveTheme === LogzioTheme.Dark ? darkV2Theme : lightV2Theme;

  useEffect(() => {
    if (targetDocument?.documentElement) {
      applyThemeToDocument(targetDocument, effectiveTheme);
    }
  }, [effectiveTheme, targetDocument]);

  return (
    <ThemeContext.Provider
      value={{
        currentTheme: effectiveTheme,
        toggleTheme,
        setTheme,
      }}
    >
      <StyledThemeProvider theme={themeObject}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
};
